import React from 'react';
import { Truck, ShieldCheck, Gem, RotateCcw, Award, TrendingUp, Users, Star, ArrowRight } from 'lucide-react';

const VALUE_PROPS = [
  { icon: Truck, title: 'Fast delivery', copy: 'Nairobi in 24 hours, nationwide within the week.' },
  { icon: ShieldCheck, title: 'Secure payment', copy: 'M-Pesa, card and bank transfer, all encrypted end to end.' },
  { icon: Gem, title: 'Premium materials', copy: 'Full-grain leather and solid hardware, chosen to age well.' },
  { icon: RotateCcw, title: 'Easy returns', copy: '14 days to change your mind, no questions asked.' },
];

const ABOUT_POINTS = [
  { icon: Award, title: 'Considered craft', copy: 'Every piece is built from materials chosen for how they wear in, not just out.' },
  { icon: TrendingUp, title: 'Designed to be worn', copy: 'Shapes that hold their form through a full day of carrying.' },
  { icon: Users, title: 'A team that answers', copy: 'Real people in Nairobi, reachable by phone, WhatsApp or email.' },
];

const TESTIMONIALS = [
  { name: 'Wanjiru Kamau', role: 'Verified purchase', quote: 'The tote has been through two rainy seasons and still looks new. Worth every shilling.' },
  { name: 'Brian Otieno', role: 'Verified purchase', quote: 'Ordered on a Tuesday, wearing them by Thursday. Delivery to Kisumu was faster than I expected.' },
  { name: 'Amina Yusuf', role: 'Verified purchase', quote: 'The leather smell alone tells you this isn’t a copy. My go-to gift for the last year.' },
  { name: 'Kevin Mwangi', role: 'Verified purchase', quote: 'M-Pesa checkout took under a minute. Customer support sorted a size swap same day.' },
];

const STATS = [
  { value: '10k+', label: 'Pieces carried' },
  { value: '4.9/5', label: 'Average rating' },
  { value: '98%', label: 'On-time delivery' },
  { value: '24/7', label: 'Support' },
];

const renderStars = () => Array.from({ length: 5 }, (_, i) => <Star key={i} className="w-3.5 h-3.5 text-brass fill-current" />);

const BrandStory = ({ sectionsRef, onOpenContact }) => (
  <>
    {/* Value props */}
    <section className="bg-surface py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {VALUE_PROPS.map(({ icon: Icon, title, copy }) => (
          <div key={title}>
            <div className="w-11 h-11 rounded-full bg-accent-soft flex items-center justify-center mb-4">
              <Icon className="w-5 h-5 text-accent" />
            </div>
            <h3 className="font-display text-lg text-ink mb-1.5">{title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed">{copy}</p>
          </div>
        ))}
      </div>
    </section>

    {/* About */}
    <section ref={(el) => { sectionsRef.current.about = el; }} className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-16">
          <p className="dpc-label text-accent mb-3">About DPC Collection</p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-ink leading-tight mb-5">
            Built for the everyday carry, not the display shelf.
          </h2>
          <p className="text-ink-soft leading-relaxed">
            We work with small tanneries and workshops across the region to put out bags and shoes that hold
            up to a real week — the commute, the market, the meeting that runs long. No plans, no pressure,
            just pieces you reach for without thinking twice.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
          {ABOUT_POINTS.map(({ icon: Icon, title, copy }) => (
            <div key={title} className="text-center sm:text-left">
              <div className="w-11 h-11 rounded-full bg-ink flex items-center justify-center mb-4 mx-auto sm:mx-0">
                <Icon className="w-5 h-5 text-paper" />
              </div>
              <h3 className="font-display text-lg text-ink mb-1.5">{title}</h3>
              <p className="text-sm text-ink-soft leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonials */}
    <section ref={(el) => { sectionsRef.current.testimonials = el; }} className="bg-ink py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <p className="dpc-label text-brass mb-3">Stories</p>
        <h2 className="font-display text-3xl sm:text-4xl text-paper leading-tight mb-14 max-w-xl">
          What people say after the first month of carrying one.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-16">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-paper/[0.06] border border-paper/10 rounded-md p-6">
              <div className="flex items-center gap-1 mb-3">{renderStars()}</div>
              <p className="text-paper/90 leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-brass/20 text-brass flex items-center justify-center font-display text-sm">
                  {t.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div>
                  <p className="text-paper text-sm font-medium">{t.name}</p>
                  <p className="text-paper/50 text-xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-paper/10">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-3xl sm:text-4xl text-paper mb-1">{s.value}</p>
              <p className="dpc-label text-paper/50">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Contact CTA */}
    <section className="bg-accent py-16 sm:py-20">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-display text-3xl sm:text-4xl text-paper mb-4">Have a question before you order?</h2>
        <p className="text-paper/85 mb-8">We answer most messages within the hour, most days before lunch.</p>
        <button
          onClick={onOpenContact}
          className="inline-flex items-center gap-2 bg-ink text-paper dpc-label px-7 py-3.5 rounded-full hover:bg-paper hover:text-ink transition-colors"
        >
          Get in touch <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  </>
);

export default BrandStory;
